import type { Language } from '@nxt-insurance/constants'
import { SupportedMarketKey } from '@nxt-insurance/constants'

import { success } from '@flows/configs/de/incomeProtection/steps/success'
import { getFlowConfiguration } from '@flows/configs/shared/flowConfiguration'
import {
  appointmentDetailsSchema,
  appointment_details,
} from '@flows/configs/shared/mgaLight/steps/appointmentDetails'
import { getLoginSchema, login } from '@flows/configs/shared/mgaLight/steps/login'
import { phoneNumberConfirmation } from '@flows/configs/shared/mgaLight/steps/phoneConfirmation'
import { progressFirstStep } from '@flows/configs/shared/mgaLight/steps/progressFirstStep'
import type { AnySteps } from '@flows/core/types/stepTypes'
import { generateGetFlowValueFunctions } from '@flows/insurance/utils'

import { employmentStatus } from './steps/employmentStatus'
import { hasIncomeProtection } from './steps/hasIncomeProtection'
import { illness } from './steps/illness'
import { income } from './steps/income'
import { progressLastStep } from './steps/progressLastStep'
import { incomeProtectionSchema } from './types'

const helperFunctions = generateGetFlowValueFunctions('mga_occupational_disability_de')

const schema = incomeProtectionSchema.merge(getLoginSchema()).merge(appointmentDetailsSchema)

export const getIncomeProtectionConfig = (args: {
  getCurrentLanguage: () => Language
  platform: 'web' | 'native'
  environment: string
}) => {
  const params = {
    helperFunctions,
    schema,
    getCurrentLanguage: args.getCurrentLanguage,
    platform: args.platform,
    environment: args.environment,
  }
  const steps = {
    // TODO: This step is temporarily not used. But we don't plan to completely remove it, therefore the code should remain here
    // This will be re-checked in the scope of this ticket: https://hellogetsafe.atlassian.net/browse/IA-540
    progress_first_step: progressFirstStep({
      platform: args.platform,
      nextStep: 'has_income_protection',
    }),
    has_income_protection: hasIncomeProtection(params),
    employment_status: employmentStatus(params),
    income: income(params),
    illness: illness(params),
    login: login({ market: SupportedMarketKey.De, platform: args.platform }),
    phone_confirmation: phoneNumberConfirmation({ market: SupportedMarketKey.De }),
    progress_last_step: progressLastStep(params),
    success: success(params),
    appointment_details: appointment_details({
      type: 'translatableText',
      translationKey: 'sharedFlows_incomeProtection:title',
    }),
  } as const satisfies AnySteps

  return getFlowConfiguration(steps, {
    constants: {
      flowName: 'income-protection',
      SCHEMA: schema,
      FIRST_STEP_NAME: 'has_income_protection',
      productKey: 'mga_occupational_disability_de',
    },
  })
}
